import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { recordsContext } from '../../extensions/bundled/documents/documentsStore';
import { isWholeBody } from '../../extensions/bundled/documents/docBodyHead';
import { subscribeDocumentChanges } from '../../extensions/bundled/documents/documentsUiStore';
import { useAuthStore } from '../../store/authStore';
import { useTeamPresenceStore, selectDocumentViewers, selectBlockPresence } from '../../store/teamPresenceStore';
import { pushTeamPresenceContext, pushCollabOps, addTeamEventListener, COLLAB_BLOCK_OPS_EVENT } from '../../services/teamChannelService';
import { userColor } from '../../utils/collabColors';
import { stableStringify, reconcileConflictsWithBlock } from '../ui/lexicalBlocks';
import { useDocumentBlockReconcile } from './useDocumentBlockReconcile';

// A record reconcile (docbody delta) may only apply while the client has been
// quiet — no local edit scheduling a save, no inbound live op applied — for
// at least this long. During active editing an incoming record is guaranteed
// stale (it necessarily predates whatever's in flight), so applying it would
// only revert/rubber-band live content; see attemptReconcile below.
const RECONCILE_RECENCY_GATE_MS = 1500;
// Retry delay for a reconcile stashed because the client was still active —
// deliberately a bit longer than the gate so a burst of edits collapses into
// one retry instead of one per keystroke.
const RECONCILE_RETRY_MS = 1600;
// COMMS-17: how long the "you're both editing this paragraph" conflict
// banner stays visible before auto-dismissing.
const COLLAB_CONFLICT_BANNER_MS = 5000;

/**
 * The live half of an open document (moved out of DocumentEditorTab.js,
 * documents redesign 13.10.2026 — a pure move): the authoritative docbody
 * reconcile for every scope, plus — for team documents only — presence and
 * block-level collab over the team channel. Personal documents never touch
 * the team half: `collab` stays null and no presence/ops traffic is sent.
 *
 * `session` is useDocumentBody's return value; both halves share its refs.
 */
export function useDocumentCollab(session, { documentId, scope }) {
  const isTeamDoc = scope.type === 'team' && scope.teamId != null;
  const {
    editorRef,
    bodyHtmlRef,
    bodyBlocksRef,
    bodyTimer,
    bodySaveInFlightRef,
    applyingRemoteRef,
    applyRemote,
    savedLexicalRingRef,
    lastLocalEditAtRef,
    lastOpsAppliedAtRef,
    editorFocusedRef,
    syncCache,
    notifyBody,
    setInitialBody,
    setInitialBlocks,
    setEditorNonce,
    noteRemoteBlocks,
  } = session;

  // COMMS-17: non-null while the "you and someone else are both editing this
  // paragraph" banner is showing — see attemptReconcile below for when it's
  // set. Purely informational.
  const [collabConflict, setCollabConflict] = useState(null);
  // Mirrors the caret's current block id, kept current by collab.onActiveBlock
  // below. Read synchronously by attemptReconcile so it can detect a
  // same-block conflict without needing a live editor instance.
  const activeBlockIdRef = useRef(null);
  const collabConflictTimerRef = useRef(null);
  // Latest docbody record deferred by the recency gate (superseded by any
  // newer arrival) and the single retry timer used to re-attempt it.
  const pendingReconcileRecordRef = useRef(null);
  const reconcileRetryTimerRef = useRef(null);
  const attemptReconcileRef = useRef(null);

  const scheduleReconcileRetry = useCallback(() => {
    if (reconcileRetryTimerRef.current) return;
    reconcileRetryTimerRef.current = setTimeout(() => {
      reconcileRetryTimerRef.current = null;
      const pending = pendingReconcileRecordRef.current;
      pendingReconcileRecordRef.current = null;
      if (pending) attemptReconcileRef.current?.(pending);
    }, RECONCILE_RETRY_MS);
  }, []);

  // COMMS-17: surfaces the conflict banner and auto-dismisses it —
  // re-triggering just restarts the timer rather than stacking banners.
  const showCollabConflict = useCallback(() => {
    setCollabConflict({ at: Date.now() });
    if (collabConflictTimerRef.current) clearTimeout(collabConflictTimerRef.current);
    collabConflictTimerRef.current = setTimeout(() => {
      collabConflictTimerRef.current = null;
      setCollabConflict(null);
    }, COLLAB_CONFLICT_BANNER_MS);
  }, []);

  const dismissCollabConflict = useCallback(() => {
    if (collabConflictTimerRef.current) {
      clearTimeout(collabConflictTimerRef.current);
      collabConflictTimerRef.current = null;
    }
    setCollabConflict(null);
  }, []);

  useEffect(() => () => {
    if (collabConflictTimerRef.current) {
      clearTimeout(collabConflictTimerRef.current);
      collabConflictTimerRef.current = null;
    }
  }, [documentId]);

  /** Attempts to reconcile one inbound docbody record (r.bodyLexical) into the
   *  live editor. Cheap short-circuits first (an in-flight/debouncing save of
   *  our own — that save's own future echo, or a later reconcile attempt,
   *  will supersede this stale record), then the own-echo ring buffer (this
   *  IS our own save landing back — no-op), then a recency gate: only apply
   *  while both local edits and inbound live ops have been quiet for
   *  RECONCILE_RECENCY_GATE_MS, otherwise stash this record (replacing any
   *  earlier stash) and retry once activity should have settled. */
  const attemptReconcile = useCallback((r) => {
    // Any record reaching a decision below supersedes whatever's currently
    // stashed — records are processed in arrival order. Only the busy branch
    // below re-arms the stash, with r itself.
    pendingReconcileRecordRef.current = null;
    if (bodyTimer.current || bodySaveInFlightRef.current) return;
    const json = stableStringify(r.bodyLexical);
    if (savedLexicalRingRef.current.includes(json)) return; // our own echo
    const now = Date.now();
    const busy =
      now - lastLocalEditAtRef.current < RECONCILE_RECENCY_GATE_MS ||
      now - lastOpsAppliedAtRef.current < RECONCILE_RECENCY_GATE_MS;
    if (busy) {
      pendingReconcileRecordRef.current = r;
      scheduleReconcileRetry();
      return;
    }
    // COMMS-17: applyFullBlocks will skip any op targeting the block the
    // caret is CURRENTLY in — detect that same-block concurrent edit here and
    // surface it rather than letting it stay silent.
    if (reconcileConflictsWithBlock(bodyBlocksRef.current?.blocks, r.bodyLexical, activeBlockIdRef.current)) {
      showCollabConflict();
    }
    applyRemote(() => editorRef.current?.applyFullBlocks(r.bodyLexical, { onlyIds: r.onlyIds || null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyRemote, scheduleReconcileRetry, showCollabConflict]);
  attemptReconcileRef.current = attemptReconcile;

  // Clear any pending reconcile retry when the document changes or the tab
  // unmounts — a stash targets one specific document's content.
  useEffect(() => () => {
    if (reconcileRetryTimerRef.current) {
      clearTimeout(reconcileRetryTimerRef.current);
      reconcileRetryTimerRef.current = null;
    }
    pendingReconcileRecordRef.current = null;
    // COMMS-17: a stale focused-block id from a previous document must never
    // leak into this one's conflict detection.
    activeBlockIdRef.current = null;
  }, [documentId]);

  // ── Team presence: "I have this document open" / "I'm on block X" ────────
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentUserName = useAuthStore((s) => s.user?.name || s.user?.email || '');
  const teamPresenceByTeam = useTeamPresenceStore((s) => s.byTeam);

  useEffect(() => {
    if (!isTeamDoc) return undefined;
    pushTeamPresenceContext(scope.teamId, { document_id: documentId });
    return () => pushTeamPresenceContext(scope.teamId, { document_id: null, block_id: null, block_offset: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamDoc, scope.teamId, documentId]);

  const docViewers = useMemo(
    () => (isTeamDoc ? selectDocumentViewers(teamPresenceByTeam, scope.teamId, documentId, currentUserId) : []),
    [isTeamDoc, teamPresenceByTeam, scope.teamId, documentId, currentUserId]
  );

  const remotePresence = useMemo(() => {
    if (!isTeamDoc) return [];
    return selectBlockPresence(teamPresenceByTeam, scope.teamId, { documentId }, currentUserId).map((p) => ({
      blockId: p.blockId,
      blockOffset: p.blockOffset,
      userId: p.userId,
      name: p.name,
      color: userColor(p.userId),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamDoc, teamPresenceByTeam, scope.teamId, documentId, currentUserId]);

  // ── Team collab: inbound live block ops from teammates ────────────────────
  useEffect(() => {
    if (!isTeamDoc) return undefined;
    return addTeamEventListener(COLLAB_BLOCK_OPS_EVENT, (payload, { teamId }) => {
      if (String(teamId) !== String(scope.teamId)) return;
      if (payload?.entity_type !== 'document' || payload?.entity_id !== documentId) return;
      if (!Array.isArray(payload?.ops) || !payload.ops.length) return;
      lastOpsAppliedAtRef.current = Date.now();
      applyRemote(() => editorRef.current?.applyRemoteOps(payload.ops, { actorUserId: payload.actor?.user_id }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamDoc, scope.teamId, documentId, applyRemote]);

  const collab = useMemo(() => {
    if (!isTeamDoc) return null;
    return {
      onOps: (ops) => pushCollabOps(scope.teamId, { entity_type: 'document', entity_id: documentId, ops }),
      // Orders the two texts when two people's edits to one block collide (lexicalMerge.js).
      selfUserId: currentUserId,
      onActiveBlock: (blockId, offset) => {
        // COMMS-17: mirrored into a ref (not state — this fires on every
        // caret move, throttled ~400ms) so attemptReconcile can read it.
        activeBlockIdRef.current = blockId;
        pushTeamPresenceContext(scope.teamId, {
          document_id: documentId,
          block_id: blockId,
          block_offset: blockId == null ? null : offset,
        });
      },
      onRemoteApplied: (snapshot, html, appliedIds) => {
        bodyHtmlRef.current = html;
        // What this apply changed is a teammate's, not something to save from here.
        noteRemoteBlocks(bodyBlocksRef.current, snapshot, appliedIds);
        bodyBlocksRef.current = snapshot;
        syncCache();
        notifyBody();
        applyingRemoteRef.current = false;
      },
      remotePresence,
      // Typist sees their own marker exactly as teammates do — but only when
      // someone else is actually in this document.
      selfMarker:
        docViewers.length > 0 && currentUserId != null
          ? { userId: currentUserId, name: currentUserName || 'You' }
          : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamDoc, scope.teamId, documentId, remotePresence, syncCache, docViewers, currentUserId, currentUserName]);

  // Block records (a body stored per block — docBlocks.js): only the blocks
  // that arrived are reconciled.
  const reconcileBlocks = useDocumentBlockReconcile(session, { documentId, scope, applyRemote });

  // ── Authoritative reconcile: a docbody record for this open doc arrived via
  // the WS fast path (own-device echo, or a teammate's debounced save landing).
  useEffect(() => {
    const myScopeKey = recordsContext(scope).workspaceId;
    return subscribeDocumentChanges((scopeKey, records) => {
      if (scopeKey !== myScopeKey) return;
      reconcileBlocks(records);
      for (const r of records) {
        if (r.record_type !== 'docbody' || r.scope_id !== documentId) continue;
        // A body stored per block keeps only an HTML snapshot (for older app
        // versions) on its docbody row: the blocks above are the body — unless
        // an older app saved the body whole since (documentsStore.isWholeBody).
        if (!isWholeBody(r)) continue;
        if (r.bodyLexical && r.blocks) {
          // An older app saved the body whole over a body stored per block:
          // its blocks are taken in, but it never removes a paragraph — it
          // started from a snapshot up to a minute old and cannot know the
          // ones written since (applyFullBlocks onlyIds: puts only).
          attemptReconcile({ ...r, onlyIds: new Set(r.bodyLexical.blocks.map((b) => b.id)) });
        } else if (r.bodyLexical) {
          // Own-echo guard + recency gate: see attemptReconcile above. While a
          // save WE issued is still debouncing or in flight, our local body is
          // ahead of anything a delta can reflect — applying it would revert
          // an edit to an unfocused block that has not round-tripped yet.
          attemptReconcile(r);
        } else if (!editorFocusedRef.current) {
          // Legacy remote (HTML only): safe to repaint via a full remount only
          // while unfocused — while typing, this converges on the next blur or
          // local save instead of yanking the caret.
          bodyHtmlRef.current = r.bodyHtml || '';
          bodyBlocksRef.current = null;
          setInitialBody(r.bodyHtml || '');
          setInitialBlocks(null);
          setEditorNonce((n) => n + 1);
          syncCache();
          notifyBody();
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, scope.type, scope.teamId, applyRemote, syncCache, reconcileBlocks]);

  return { collab, docViewers, collabConflict, dismissCollabConflict };
}
